import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Share2, Loader2, RefreshCw, Plus, X, AlertCircle, Calendar, Send, Trash2, Image as ImageIcon, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall } from '@/lib/api';

interface PBAccount { id: number; platform: string; username: string }

interface PostRow {
  id: string;
  organization_id: string | null;
  title: string | null;
  body: string;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  scheduled_for: string | null;
  published_at: string | null;
  social_accounts: number[];
  media_ids: string[];
  post_bridge_id: string | null;
  last_synced_at: string | null;
  created_at: string;
}

const PLATFORM_COLORS: Record<string, string> = {
  instagram: 'bg-pink-100 text-pink-900 dark:bg-pink-900/30 dark:text-pink-200',
  tiktok:    'bg-zinc-900 text-white',
  youtube:   'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200',
  x:         'bg-zinc-100 text-zinc-900',
  facebook:  'bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200',
  linkedin:  'bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-200',
  threads:   'bg-zinc-100 text-zinc-900',
  bluesky:   'bg-sky-100 text-sky-900',
  pinterest: 'bg-red-100 text-red-900',
};

export default function Social() {
  const { getToken } = useAuth();
  const [accounts, setAccounts] = useState<PBAccount[]>([]);
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const [a, p] = await Promise.all([
        apiCall<{ items: PBAccount[] }>('/v1/social/accounts', { bearerToken: token }),
        apiCall<{ items: PostRow[] }>('/v1/social', { bearerToken: token }),
      ]);
      setAccounts(a.items);
      setPosts(p.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function sync(id: string) {
    setSyncing(id);
    try {
      const token = await getToken();
      await apiCall(`/v1/social/${id}/sync`, { method: 'POST', bearerToken: token });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Sync failed'); }
    finally { setSyncing(null); }
  }

  async function remove(id: string) {
    if (!confirm('Delete this post? If scheduled, it will also be cancelled on Post Bridge.')) return;
    try {
      const token = await getToken();
      await apiCall(`/v1/social/${id}`, { method: 'DELETE', bearerToken: token });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Delete failed'); }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Share2 className="h-6 w-6" /> Social</h1>
          <p className="text-sm text-muted-foreground">
            Compose + schedule posts across your connected accounts. Powered by Post Bridge.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCompose(true)} disabled={accounts.length === 0}>
            <Plus className="mr-1 h-4 w-4" /> Compose
          </Button>
        </div>
      </div>

      {err && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>}

      {/* Connected accounts strip */}
      <Card>
        <CardContent className="py-3">
          {accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No connected accounts. Connect them at <a href="https://www.post-bridge.com/dashboard" target="_blank" rel="noopener noreferrer" className="underline">post-bridge.com</a> and they'll appear here.
            </p>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground mr-2">Connected:</span>
              {accounts.map((a) => (
                <span key={a.id} className={'text-xs px-2 py-1 rounded-full ' + (PLATFORM_COLORS[a.platform] ?? 'bg-muted text-muted-foreground')}>
                  {a.platform} · @{a.username}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!loading && posts.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No posts yet. Click <strong>Compose</strong> to publish or schedule one.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {posts.map((p) => (
          <PostRowCard
            key={p.id} post={p} accounts={accounts}
            onSync={() => void sync(p.id)}
            onDelete={() => void remove(p.id)}
            syncing={syncing === p.id}
          />
        ))}
      </div>

      {showCompose && (
        <ComposeDialog
          accounts={accounts}
          onClose={() => setShowCompose(false)}
          onPosted={() => { setShowCompose(false); void load(); }}
        />
      )}
    </div>
  );
}

function PostRowCard({ post, accounts, onSync, onDelete, syncing }: {
  post: PostRow; accounts: PBAccount[]; onSync: () => void; onDelete: () => void; syncing: boolean;
}) {
  const acctMap = new Map(accounts.map((a) => [a.id, a]));
  const targets = post.social_accounts.map((id) => acctMap.get(id)).filter((a): a is PBAccount => !!a);
  const statusBadge = post.status === 'published' ? 'default'
    : post.status === 'failed' ? 'destructive'
    : post.status === 'scheduled' ? 'outline'
    : 'outline';
  return (
    <Card>
      <CardContent className="py-3 flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={statusBadge} className="capitalize">{post.status}</Badge>
            {post.scheduled_for && post.status === 'scheduled' && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {new Date(post.scheduled_for).toLocaleString()}
              </span>
            )}
            {post.published_at && post.status === 'published' && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-600" /> {new Date(post.published_at.replace(' ', 'T') + 'Z').toLocaleString()}
              </span>
            )}
            <div className="flex items-center gap-1 flex-wrap ml-2">
              {targets.map((a) => (
                <span key={a.id} className={'text-[10px] px-1.5 py-0.5 rounded ' + (PLATFORM_COLORS[a.platform] ?? 'bg-muted text-muted-foreground')}>
                  {a.platform}/@{a.username}
                </span>
              ))}
            </div>
          </div>
          {post.title && <div className="text-sm font-medium">{post.title}</div>}
          <p className="text-sm whitespace-pre-wrap line-clamp-4">{post.body}</p>
          {post.media_ids.length > 0 && (
            <div className="text-xs text-muted-foreground flex items-center gap-1"><ImageIcon className="h-3 w-3" /> {post.media_ids.length} media item{post.media_ids.length === 1 ? '' : 's'}</div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {post.post_bridge_id && (
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onSync} disabled={syncing}>
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ComposeDialog({ accounts, onClose, onPosted }: {
  accounts: PBAccount[]; onClose: () => void; onPosted: () => void;
}) {
  const { getToken } = useAuth();
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set(accounts.length === 1 ? [accounts[0]!.id] : []));
  const [scheduled, setScheduled] = useState<string>('');
  const [mode, setMode] = useState<'now' | 'schedule' | 'draft'>('now');
  const [mediaIds, setMediaIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(id: number) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function uploadFile(file: File) {
    const allowed = ['image/png', 'image/jpeg', 'video/mp4', 'video/quicktime', 'application/pdf'];
    if (!allowed.includes(file.type)) { setErr(`Unsupported file type: ${file.type}`); return; }
    setUploading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<{ upload_url: string; media_id: string }>('/v1/social/upload-url', {
        method: 'POST',
        bearerToken: token,
        body: { mime_type: file.type, size_bytes: file.size, name: file.name },
      });
      const put = await fetch(r.upload_url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error('upload failed');
      setMediaIds((m) => [...m, r.media_id]);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Upload failed'); }
    finally { setUploading(false); }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (selected.size === 0) { setErr('Pick at least one account'); return; }
    if (mode === 'schedule' && !scheduled) { setErr('Pick a date/time'); return; }
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/social', {
        method: 'POST',
        bearerToken: token,
        body: {
          body: body.trim(),
          social_account_ids: [...selected],
          media_ids: mediaIds,
          status: mode === 'draft' ? 'draft' : undefined,
          scheduled_for: mode === 'schedule' ? new Date(scheduled).toISOString() : null,
        },
      });
      onPosted();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Compose post</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>

            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground mb-1">Caption *</span>
              <textarea
                value={body} onChange={(e) => setBody(e.target.value)}
                required maxLength={2200} rows={5}
                placeholder="What's happening at UWC?"
                className="w-full border rounded px-3 py-2 text-sm resize-none"
              />
              <span className="text-[10px] text-muted-foreground tabular-nums">{body.length} / 2200</span>
            </label>

            <div>
              <span className="block text-xs text-muted-foreground mb-1">Post to *</span>
              <div className="flex flex-wrap gap-1.5">
                {accounts.map((a) => (
                  <button
                    type="button" key={a.id}
                    onClick={() => toggle(a.id)}
                    className={
                      'text-xs px-2 py-1 rounded-full border ' +
                      (selected.has(a.id)
                        ? (PLATFORM_COLORS[a.platform] ?? 'bg-primary text-primary-foreground') + ' border-transparent'
                        : 'bg-background text-muted-foreground border-input hover:border-primary')
                    }
                  >
                    {a.platform} · @{a.username}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="block text-xs text-muted-foreground mb-1">Media (optional)</span>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs border rounded px-2 py-1 cursor-pointer hover:bg-muted/40">
                  {uploading ? <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> : <ImageIcon className="inline h-3 w-3 mr-1" />}
                  Add file
                  <input
                    type="file" className="hidden"
                    accept="image/png,image/jpeg,video/mp4,video/quicktime,application/pdf"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadFile(f); e.currentTarget.value = ''; }}
                    disabled={uploading}
                  />
                </label>
                {mediaIds.map((id, i) => (
                  <span key={id} className="text-[10px] px-1.5 py-0.5 rounded bg-muted">
                    #{i + 1} <code className="text-muted-foreground">{id.slice(0, 8)}…</code>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
              {(['now', 'schedule', 'draft'] as const).map((m) => (
                <button
                  type="button" key={m}
                  onClick={() => setMode(m)}
                  className={
                    'text-xs px-3 py-1 rounded-full border ' +
                    (mode === m ? 'bg-primary text-primary-foreground border-transparent' : 'border-input bg-background hover:border-primary')
                  }
                >
                  {m === 'now' ? 'Publish now' : m === 'schedule' ? 'Schedule' : 'Save draft'}
                </button>
              ))}
              {mode === 'schedule' && (
                <input
                  type="datetime-local" value={scheduled} onChange={(e) => setScheduled(e.target.value)}
                  className="text-xs border rounded px-2 py-1 ml-2"
                />
              )}
            </div>

            {err && <p className="text-sm text-destructive">{err}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={!body.trim() || selected.size === 0 || submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === 'now' ? <><Send className="h-4 w-4 mr-1" /> Publish</> : mode === 'schedule' ? 'Schedule' : 'Save draft'}
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
